import { HttpClient, HttpContext, HttpEvent, HttpEventType, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { filter, map, Observable, tap, firstValueFrom } from 'rxjs';

type OptionsType = {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  context?: HttpContext;
  observe?: any;
  params?:
    | HttpParams
    | {
        [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: any;
  withCredentials?: boolean;
};

interface PartialFileData {
  startByte: number;
  endByte: number;
  data: Blob;
  fileSize: number;
  totalParts: number;
  partNumber: number;
}

export abstract class ApiServiceBase {
  private readonly httpClient = inject(HttpClient);

  private _baseUrl = '/api';

  protected buildUrl(path: string): string {
    return `${this._baseUrl}/${path}`;
  }

  protected get<TResponse>(path: string, options?: OptionsType): Observable<TResponse> {
    return this.httpClient.get<TResponse>(`${this._baseUrl}/${path}`, { ...{ withCredentials: true }, ...options });
  }

  protected getBlob(path: string, options?: OptionsType): Observable<ArrayBuffer> {
    return this.httpClient.get(`${this._baseUrl}/${path}`, {
      ...{ withCredentials: true, responseType: 'blob' },
      ...options,
    });
  }

  protected post<TResponse>(path: string, body: any, options?: OptionsType): Observable<TResponse> {
    return this.httpClient.post<TResponse>(`${this._baseUrl}/${path}`, body, {
      ...{ withCredentials: true },
      ...options,
    });
  }

  /**
   * This posts data and receives raw http events including upload progress events
   * @param path The url path
   * @param body The data to send
   * @param options Request options
   * @returns Http events including HttpEventType.UploadProgress and HttpEventType.Response
   */
  protected postProgress<TResponse>(path: string, body: any, options?: OptionsType): Observable<HttpEvent<TResponse>> {
    return this.httpClient.post<TResponse>(`${this._baseUrl}/${path}`, body, {
      ...{ withCredentials: true },
      ...options,
      observe: 'events',
      reportProgress: true,
    });
  }

  /**
   * This posts data with a callback returning the expected data object
   * @param path The url path
   * @param body The data to send
   * @param progressFn A function to call on progress events
   * @param options Request options
   * @returns The response data
   */
  protected postProgressCallback<TResponse>(
    path: string,
    body: any,
    progressFn?: (bytesUploaded: number) => void,
    options?: OptionsType
  ): Observable<TResponse> {
    return this.postProgress<TResponse>(path, body, options).pipe(
      tap(event => {
        if (event.type === HttpEventType.UploadProgress) {
          if (progressFn) {
            progressFn(event.loaded);
          }
        }
      }),
      filter(event => event.type === HttpEventType.Response),
      map(event => {
        if (event.type === HttpEventType.Response && event.body) {
          return event.body;
        }

        return {} as TResponse;
      })
    );
  }

  protected put<TResponse>(path: string, body: any, options?: OptionsType): Observable<TResponse> {
    return this.httpClient.put<TResponse>(`${this._baseUrl}/${path}`, body, {
      ...{ withCredentials: true },
      ...options,
    });
  }

  protected delete<TResponse>(path: string, options?: OptionsType) {
    return this.httpClient.delete<TResponse>(`${this._baseUrl}/${path}`, { ...{ withCredentials: true }, ...options });
  }

  /**
   * Uploads a file in chunks with progress tracking
   * @param file The file to upload
   * @param uploadPath The path for uploading individual chunks
   * @param completePath The path for completing the upload
   * @param chunkSize Size of each chunk in bytes (default: 100MB)
   * @param progressCallback Callback for progress updates (0-100)
   * @param additionalFormData Additional form data to include in completion request
   * @param additionalCompletionFiles Additional files to include only in the completion request (e.g. thumbnail)
   * @returns Promise that resolves with the completion response
   */
  protected async postChunked<TResponse>(
    file: File,
    uploadPath: string,
    completePath: string,
    chunkSize: number = 1024 * 1024 * 100, // 100MB default
    progressCallback?: (progress: number) => void,
    additionalFormData?: { [key: string]: string },
    additionalCompletionFiles?: { [key: string]: File }
  ): Promise<TResponse> {
    const totalChunks = Math.ceil(file.size / chunkSize);
    let uploadedChunks = 0;

    // Upload chunks sequentially
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('fileName', file.name);
      formData.append('chunkNumber', (chunkIndex + 1).toString());
      formData.append('totalChunks', totalChunks.toString());

      // Add any additional form data for chunk upload
      if (additionalFormData) {
        Object.keys(additionalFormData).forEach(key => {
          formData.append(key, additionalFormData[key]);
        });
      }

      await firstValueFrom(this.post<void>(uploadPath, formData));

      uploadedChunks++;
      if (progressCallback) {
        // Reserve 10% for completion step
        const progress = Math.round((uploadedChunks / totalChunks) * 90);
        progressCallback(progress);
      }
    }

    // Complete the chunked upload
    const completeFormData = new FormData();
    completeFormData.append('fileName', file.name);
    completeFormData.append('totalChunks', totalChunks.toString());
    completeFormData.append('totalSize', file.size.toString());

    // Add any additional form data for completion
    if (additionalFormData) {
      Object.keys(additionalFormData).forEach(key => {
        completeFormData.append(key, additionalFormData[key]);
      });
    }

    // Add any additional files for completion only (e.g. thumbnail override)
    if (additionalCompletionFiles) {
      Object.keys(additionalCompletionFiles).forEach(key => {
        completeFormData.append(key, additionalCompletionFiles[key]);
      });
    }

    const result = await firstValueFrom(this.post<TResponse>(completePath, completeFormData));

    if (progressCallback) {
      progressCallback(100);
    }

    return result;
  }

  /**
   * Uploads multiple files in chunks with progress tracking
   * @param files Array of files to upload
   * @param uploadPath The path for uploading individual chunks
   * @param completePath The path for completing each upload
   * @param chunkSize Size of each chunk in bytes (default: 100MB)
   * @param progressCallback Callback for overall progress updates (0-100)
   * @param fileProgressCallback Callback for individual file progress with file info
   * @param additionalFormData Additional form data to include in completion request
   * @returns Promise that resolves with array of completion responses
   */
  protected async postMultipleChunked<TResponse>(
    files: File[],
    uploadPath: string,
    completePath: string,
    chunkSize: number = 1024 * 1024 * 100, // 100MB default
    progressCallback?: (progress: number) => void,
    fileProgressCallback?: (fileIndex: number, fileName: string, progress: number) => void,
    additionalFormData?: { [key: string]: string }
  ): Promise<TResponse[]> {
    const results: TResponse[] = [];

    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
      const file = files[fileIndex];

      const result = await this.postChunked<TResponse>(
        file,
        uploadPath,
        completePath,
        chunkSize,
        fileProgress => {
          if (fileProgressCallback) {
            fileProgressCallback(fileIndex, file.name, fileProgress);
          }
          if (progressCallback) {
            const overallProgress = Math.round(((fileIndex + fileProgress / 100) / files.length) * 100);
            progressCallback(overallProgress);
          }
        },
        additionalFormData
      );

      results.push(result);
    }

    return results;
  }
}
